import axios from 'axios';
import base_url from '../config.js';
/**
 * Servicio para interactuar con el API de Image Records
 */
class ImageRecordsService {

  constructor() {
    this.uploadStartTime = null;
    this.uploadEndTime = null;
  }

  /**
   * Sube una imagen al servidor
   * @param {Object} image - Objeto de imagen
   * @param {string} tenantId - ID del tenant para autenticación
   * @returns {Promise} - Promesa con la respuesta del servidor
   */
  uploadImage(image, params = {}) {
    this.uploadStartTime = Date.now();

    try {

      // Crear FormData para enviar el archivo
      const formData = new FormData();
      formData.append('record', image.file, image.nombre);
      const url = '/records';
      console.log(`[ImageRecords] Starting API call to https://img-records.siga-ltda.click/records`);


      let queryString = '';

      if (Object.keys(params).length > 0) {
        const paramKeys = [];
        Object.keys(params).map(key => {
          paramKeys.push(key + '=' + params[key]);
          return paramKeys;
        });
        queryString = '?' + paramKeys.join('&');
      }

      // Log específico para uploads de archivos
      console.log(`[File Upload] Starting upload to ${url}`, {
        hasFormData: formData instanceof FormData,
        params: params,
        timeout: 60000 // 60 segundos para uploads
      });

      return axios.post(`https://img-records.siga-ltda.click/records${queryString}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Access-Control-Allow-Origin': '*'
        },
        timeout: 60000 // 60 segundos específicamente para uploads
      });

    } catch (error) {
      this.uploadEndTime = Date.now();
      const uploadDuration = this.uploadEndTime - this.uploadStartTime;

      console.error(`[ImageRecords] Upload failed after ${uploadDuration}ms`, {
        error: error.message,
        errorType: error.constructor.name,
        isTimeout: error.code === 'ECONNABORTED',
        imageName: image.nombre || 'unnamed',
        errorDetails: error
      });

      throw error;
    }
  }

  /**
   * Obtiene una imagen por su UUID
   * @param {string} recordUuid - UUID del registro
   * @param {string} tenantId - ID del tenant para autenticación
   * @returns {Promise} - Promesa con los datos de la imagen
   */
  async getImage(recordUuid, tenantId) {
    try {
      const response = await apiClient.get(`https://img-records.siga-ltda.click/records/${recordUuid}`, { tenantId });
      return response;
    } catch (error) {
      console.error('Error en getImage:', error);
      throw error;
    }
  }

  /**
   * Obtiene una imagen en formato base64 por su UUID
   * @param {string} recordUuid - UUID del registro
   * @param {string} tenantId - ID del tenant para autenticación
   * @returns {Promise} - Promesa con los datos de la imagen en base64
   */
  async getImageBase64(recordUuid, tenantId) {
    try {
      const response = await apiClient.get(`https://img-records.siga-ltda.click/records/${recordUuid}/base64`, { tenantId });
      return response;
    } catch (error) {
      console.error('Error en getImageBase64:', error);
      throw error;
    }
  }

  /**
   * Obtiene información de una imagen por su UUID
   * @param {string} recordUuid - UUID del registro
   * @param {string} tenantId - ID del tenant para autenticación
   * @returns {Promise} - Promesa con los metadatos de la imagen
   */
  async getImageInfo(recordUuid, tenantId) {
    try {
      const response = await apiClient.get(`https://img-records.siga-ltda.click/records/${recordUuid}/info`, { tenantId });
      return response;
    } catch (error) {
      console.error('Error en getImageInfo:', error);
      throw error;
    }
  }

  /**
   * Analiza una imagen usando LLM
   * @param {string} recordUuid - UUID del registro
   * @param {string} tenantId - ID del tenant para autenticación
   * @param {string} prompt - Prompt para el análisis (opcional)
   * @returns {Promise} - Promesa con el resultado del análisis
   */
  async analyzeImage(recordUuid, tenantId, prompt = 'Describe esta imagen') {
    try {
      const response = await apiClient.create(`https://img-records.siga-ltda.click/records/${recordUuid}/analyze?tenantId=${tenantId}`, { prompt });
      return response;
    } catch (error) {
      console.error('Error en analyzeImage:', error);
      throw error;
    }
  }

  /**
   * Lista los registros de imágenes con paginación
   * @param {string} tenantId - ID del tenant para autenticación
   * @param {number} page - Número de página (default: 1)
   * @param {number} perPage - Registros por página (default: 10)
   * @returns {Promise} - Promesa con la lista de registros
   */
  async listImages(tenantId, page = 1, perPage = 10) {
    try {
      const response = await apiClient.get('https://img-records.siga-ltda.click/records', {
        tenantId,
        page,
        per_page: perPage
      });
      return response;
    } catch (error) {
      console.error('Error en listImages:', error);
      throw error;
    }
  }

  /**
   * Verifica el estado de salud del servicio
   * @returns {Promise} - Promesa con el estado del servicio
   */
  async healthCheck() {
    try {
      const response = await apiClient.get('/image-records/health');
      return response;
    } catch (error) {
      console.error('Error en healthCheck:', error);
      throw error;
    }
  }
}

export default new ImageRecordsService();
